import React, {useState} from 'react'
import SettingsPanel from 'organization/Event/DashboardConfig/ComponentConfigPanel/SettingsPanel'
import TextField from 'lib/ui/TextField'
import InputLabel from 'lib/ui/InputLabel'
import TextEditor, {TextEditorContainer} from 'lib/ui/form/TextEditor'
import {useArea} from 'organization/Event/Area/AreaProvider'
import {Area} from 'organization/Event/AreasProvider'
import ComponentConfig, {
  CancelButton,
  Footer,
  SaveButton,
} from 'organization/Event/DashboardConfig/ComponentConfigPanel'
import {Controller, useForm, UseFormMethods} from 'react-hook-form'
import Switch from 'lib/ui/form/Switch'
import {onChangeCheckedHandler} from 'lib/dom'
import {Group, Tag} from 'organization/Event/AttendeesProvider'
import RulesPanel from 'organization/Event/DashboardConfig/ComponentConfigPanel/RulesPanel'
import Button from 'lib/ui/Button'
import GroupInput from 'lib/ui/form/GroupInput'
import TagsInput from 'lib/ui/form/TagsInput'

export default function AreaRegistrationConfig(props: {
  showing: boolean
  onClose: () => void
}) {
  const {area, update, processing} = useArea()

  const registerExistingAttendeesOnly = useRegisterExistingAttendeesOnly()

  const {showing, onClose} = props

  const {control, handleSubmit, register, formState, watch} = useForm()

  const [tags, setTags] = useState<Tag[]>(area.template?.tags ?? [])
  const [groups, setGroups] = useState<Group[]>(area.template?.groups ?? [])

  const addGroup = () => {
    const added = [...groups, {key: '', value: ''}]
    setGroups(added)
  }

  const updateGroup = (targetIndex: number) => (updated: Group) => {
    const updatedList = groups.map((g, i) => {
      const isTarget = i === targetIndex
      if (isTarget) {
        return updated
      }

      return g
    })

    setGroups(updatedList)
  }

  const removeGroup = (targetIndex: number) => () => {
    const updated = [...groups]
    updated.splice(targetIndex, 1)
    setGroups(updated)
  }

  const save = (data: Area['template']) => {
    if (processing) {
      return
    }

    registerExistingAttendeesOnly.save()

    update({
      ...area,
      template: {
        ...data,
        tags,
        groups,
      },
    }).then(onClose)
  }

  return (
    <ComponentConfig
      title="Registration"
      onSubmit={handleSubmit(save)}
      hasChanges={formState.isDirty}
      showing={showing}
      onClose={onClose}
    >
      <SettingsPanel>
        <Switch
          checked={registerExistingAttendeesOnly.value}
          disabled={processing}
          onChange={onChangeCheckedHandler(registerExistingAttendeesOnly.set)}
          aria-label="toggle register existing attendees only"
          labelPlacement="end"
          label={
            registerExistingAttendeesOnly.value
              ? 'Registration is Limited to Existing Attendees'
              : 'Registration is Open to All'
          }
        />
        <InputLabel>Description</InputLabel>
        <TextEditorContainer>
          <Controller
            name="description"
            defaultValue={area.template?.description || ''}
            control={control}
            render={({value, onChange}) => (
              <TextEditor
                data={value}
                onChange={onChange}
                disabled={processing}
              />
            )}
          />
        </TextEditorContainer>

        <NameTextField
          name="firstNameLabel"
          defaultValue={area.template?.firstNameLabel}
          register={register}
          disabled={processing}
          label="First Name Label"
          aria-label="registration first name label"
          hidden={registerExistingAttendeesOnly.value}
        />

        <NameTextField
          name="lastNameLabel"
          defaultValue={area.template?.lastNameLabel}
          register={register}
          disabled={processing}
          label="Last Name Label"
          aria-label="registration last name label"
          hidden={registerExistingAttendeesOnly.value}
        />

        <TextField
          name="emailLabel"
          defaultValue={area.template?.emailLabel}
          label="Email Label"
          aria-label="registration email label"
          inputProps={{
            ref: register,
          }}
          fullWidth
          disabled={processing}
        />

        <AttendeeNotFoundMessageTextField
          register={register}
          disabled={processing}
          hidden={!registerExistingAttendeesOnly.value}
        />

        <TextField
          name="joinButtonText"
          defaultValue={area.template?.joinButtonText}
          label="Join Button Text"
          aria-label="registration join button label"
          inputProps={{
            ref: register,
          }}
          fullWidth
          disabled={processing}
        />

        <CollectPhoneNumber
          register={register}
          control={control}
          watch={watch}
          disabled={processing}
        />
      </SettingsPanel>

      <RulesPanel>
        <>
          <TagsInput
            value={tags}
            onChange={setTags}
            name="tags"
            aria-label="tags"
            label="Tags"
          />
          {groups.map((group, index) => (
            <GroupInput
              key={index}
              group={group}
              onChange={updateGroup(index)}
              remove={removeGroup(index)}
            />
          ))}
          <Button
            aria-label="add group"
            variant="outlined"
            color="primary"
            onClick={addGroup}
            fullWidth
            size="large"
          >
            Add Group
          </Button>
        </>
      </RulesPanel>

      <Footer>
        <SaveButton disabled={processing} />
        <CancelButton onClick={onClose} />
      </Footer>
    </ComponentConfig>
  )
}

function useRegisterExistingAttendeesOnly() {
  const {setRegisterExistingAttendeesOnly: save, area} = useArea()
  const [value, setValue] = useState(area.register_existing_attendees_only)

  const handleSave = () => {
    if (value === area.register_existing_attendees_only) {
      // No change
      return
    }

    save(value)
  }

  return {value, set: setValue, save: handleSave}
}

function NameTextField(props: {
  register: UseFormMethods['register']
  disabled: boolean
  label: string
  'aria-label': string
  name: string
  defaultValue?: string
  hidden: boolean
}) {
  const {register, disabled, label, name, defaultValue, hidden} = props

  if (hidden) {
    return null
  }

  return (
    <TextField
      name={name}
      defaultValue={defaultValue}
      label={label}
      aria-label={props['aria-label']}
      inputProps={{
        ref: register,
      }}
      fullWidth
      disabled={disabled}
    />
  )
}

function AttendeeNotFoundMessageTextField(props: {
  register: UseFormMethods['register']
  disabled: boolean
  hidden: boolean
}) {
  const {register, disabled, hidden} = props

  const {area} = useArea()

  if (hidden) {
    return null
  }

  return (
    <TextField
      name="missingRegistrationAttendeeMessage"
      defaultValue={area.template?.missingRegistrationAttendeeMessage}
      label="Attendee Not Found Message"
      aria-label="attendee not found message"
      inputProps={{
        ref: register,
      }}
      fullWidth
      disabled={disabled}
    />
  )
}

function CollectPhoneNumber(props: {
  disabled: boolean
  register: UseFormMethods['register']
  control: UseFormMethods['control']
  watch: UseFormMethods['watch']
}) {
  const {disabled, register, control, watch} = props

  const {area} = useArea()
  const hasPhoneNumberField = watch(
    'hasPhoneNumberField',
    area.template?.hasPhoneNumberField,
  )

  return (
    <>
      <Controller
        name="hasPhoneNumberField"
        defaultValue={area.template?.hasPhoneNumberField ?? false}
        control={control}
        render={({onChange, value}) => (
          <Switch
            checked={value}
            disabled={disabled}
            onChange={onChangeCheckedHandler(onChange)}
            aria-label="toggle collect phone number"
            labelPlacement="end"
            label="Collect Phone number"
          />
        )}
      />
      <PhoneNumberField
        register={register}
        control={control}
        disabled={disabled}
        hidden={!hasPhoneNumberField}
      />
    </>
  )
}

function PhoneNumberField(props: {
  register: UseFormMethods['register']
  control: UseFormMethods['control']
  disabled: boolean
  hidden: boolean
}) {
  const {register, control, disabled, hidden} = props

  const {area} = useArea()

  if (hidden) {
    return null
  }

  return (
    <>
      <TextField
        name="phoneNumberLabel"
        defaultValue={area.template?.phoneNumberLabel}
        label="Phone number Label"
        aria-label="phone number label"
        inputProps={{
          ref: register,
        }}
        fullWidth
        disabled={disabled}
      />
      <Controller
        name="phoneNumberRequired"
        control={control}
        defaultValue={area.template?.phoneNumberRequired ?? false}
        render={({value, onChange}) => (
          <Switch
            checked={value}
            disabled={disabled}
            onChange={onChangeCheckedHandler(onChange)}
            aria-label="toggle phone number required"
            labelPlacement="end"
            label="Phone number required"
          />
        )}
      />
    </>
  )
}
